<template>
  <div>
    <!-- Loader -->
    <portal to="loaderTarget">
      <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
        <gd-loader :label="$t('frontend_loading')"/>
      </div>
    </portal>
    
    <div class="pt-4 flex justify-center">
      <div class="w-full">
        <automate
          ref="automate"
          :appName="appName"
          :preAutomationsData="
            model.automationTriggers && model.automationTriggers.length
              ? model.automationTriggers
              : []
          "
          :postAutomationsData="
            model.automationActions && model.automationActions.length
              ? model.automationActions
              : []
          "
          @showLoader="showLoader()"
          @hideLoader="hideLoader()"
        ></automate>
      </div>
    </div>
    <portal to="headerContent">
      <div class="flex flex-row">
        <gd-button
          size="auto"
          @click="moveBackToAutomationsListing"
        >
          {{ $t('frontend_exit') }}
        </gd-button>
        <gd-button
          variant="primary"
          size="auto"
          class="ml-3"
          @click="finalizeAutomationData()"
        >
          {{ getAutomationActionTitle() }}
        </gd-button>
      </div>
    </portal>
    <portal to="headerBreadCrumb">
      <div
        class="flex h-full items-center cursor-pointer text-grey-500 hover:text-primary"
        @click="moveBackToAutomationsListing"
      >
        <ExitIcon class="w-5 h-5"/>
      </div>
    </portal>

    <gd-modal
      id="automation-modal"
      name="automation-modal"
      :title="
        this.$t('frontend_groovemail_automations_enter_name_for_automation')
      "
    >
      <div class="p-6">
        <div class="flex flex-col">
          <label class="font-semibold text-sm text-grey-700 mb-2">{{
            $t('frontend_groovemail_analytics_list_automation_name')
          }}</label>
          <text-input
            variant="base"
            v-model="model.name"
            :validator="$v.model.name"
            :placeholder="$t('frontend_groovemail_automations_enter_automation_name_label')"
          />
        </div>

        <div class="flex justify-end mt-6">
          <gd-button
            size="auto"
            @click="closeAutomationNameModal()"
          >
            {{ $t('frontend_cancel') }}
          </gd-button>
          <gd-button
            variant="primary"
            class="ml-2"
            size="auto"
            @click="triggerAutomationNameSave"
          >
            {{ getAutomationActionTitle() }}
          </gd-button>
        </div>
      </div>
    </gd-modal>
  </div>
</template>

<script>
import axios from 'axios';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import themeConfig from '@/theme/themeConfig.js';
import Automate from '@/components/automations-v2/automate.vue';
import showAlert from '@/components/sweet-alert-popup.js';

// import icons
import ExitIcon from '@/assets/images/icons/new/exit_arrow_new.svg';

export default {
  middleware: 'auth',
  layout: 'defaultWithoutSidebarLogo',
  name: 'create-automation',

  validations: {
    model: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
    },
  },

  data() {
    return {
      loaderColor: themeConfig.appPrimaryColor,
      fullPageLoader: true,
      isLoading: false,
      appName: '',
      model: {
        name: '',
        automationTriggers: [],
        automationActions: [],
        status: 1,
      },
      currentAutomation: null,
    };
  },

  async created() {
    this.currentAutomation = this.$route.params.id
      ? this.$route.params.id
      : null;
    if (this.currentAutomation) {
      await this.fetchCurrentAutomation(this.currentAutomation);
    }
  },

  mounted() {
    this.appName = this.supBrandName;
  },


  computed: {
    // Used to return brandData value from store.
    supBrandName() {
      return this.$store.getters.supBrand.title;
    },
  },

  methods: {
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    moveBackToAutomationsListing() {
      this.$router.push({ name: 'AutomationV2' });
    },

    getAutomationActionTitle() {
      let automationModalTitle = `${this.$t('frontend_save')}`;
      if (this.$route.name === 'AutomationCreate') {
        automationModalTitle = `${this.$t('frontend_save')}`;
      } else if (this.$route.name === 'AutomationEdit') {
        automationModalTitle = `${this.$t('frontend_update')}`;
      }
      return automationModalTitle;
    },

    setAutomationData(name, triggers, actions, status) {
      this.model.name = name || '';
      this.model.automationTriggers =
        triggers && triggers.length ? triggers : [];
      this.model.automationActions = actions && actions.length ? actions : [];
      this.model.status = status || 0;
    },

    finalizeAutomationData() {
      const automationsValid = this.checkAutomationsValid();
      if (automationsValid) {
        this.$root.$emit('modal-open', 'automation-modal');
      }
    },

    triggerAutomationNameSave() {
      this.$v.model.$touch();
      if (!this.$v.model.name.$invalid) {
        if (this.currentAutomation) {
          this.updateAutomationData();
        } else {
          this.saveAutomationData();
        }
      }
    },

    async saveAutomationData() {
      const autoRequestBody = this.getAutomationRequestBody();
      this.showLoader();
      try {
        await axios.post('/automation_v2', autoRequestBody);
        this.moveBackToAutomationsListing();
        this.hideLoader();
      } catch (err) {
        this.hideLoader();
      }
    },

    async updateAutomationData() {
      const autoRequestBody = this.getAutomationRequestBody();
      this.showLoader();
      try {
        await axios.put(
          `/automation_v2/${this.currentAutomation}`,
          autoRequestBody
        );
        this.moveBackToAutomationsListing();
        this.hideLoader();
      } catch (err) {
        this.hideLoader();
      }
    },

    async fetchCurrentAutomation(automationId) {
      this.showLoader();
      try {
        const automationData = await axios.get(
          `automation_v2/${automationId}/show`
        );
        if (automationData && automationData.data) {
          this.setAutomationData(
            automationData.data.name,
            automationData.data.pre_condition,
            automationData.data.post_condition,
            automationData.data.status
          );
        }
        this.hideLoader();
      } catch (err) {
        this.hideLoader();
      }
    },

    getAutomationRequestBody() {
      const automationFilterData = [
        ...this.$refs.automate.fetchAutomationsFilterData(),
      ];
      const triggersConditions = [];
      const actions = [];
      automationFilterData.forEach((element) => {
        const autoEle = { ...element };
        delete autoEle.filterValid;
        if (autoEle.type === 'action') {
          delete autoEle.type;
          actions.push(autoEle);
        } else {
          delete autoEle.type;
          triggersConditions.push(autoEle);
        }
      });
      return {
        name: this.model.name,
        pre_condition: triggersConditions,
        post_condition: actions,
        status: this.model.status,
      };
    },

    checkAutomationsValid() {
      let automationValid = false;
      const automationFilterData = [
        ...this.$refs.automate.fetchAutomationsFilterData(),
      ];
      const automationFiltersObj =
        this.checkAutomationFiltersConfigured(automationFilterData);
      if (
        automationFilterData &&
        automationFilterData.length &&
        automationFilterData.length > 1 &&
        automationFiltersObj.filtersConfigured &&
        automationFiltersObj.filtersHasActions
      ) {
        if (automationFilterData.every((filter) => filter.saved === 1)) {
          automationValid = true;
        } else {
          this.showErrorAlert(
            'frontend_groovemail_automations_error_save_nodes_before_main'
          );
        }
      }
      if (
        !automationFilterData ||
        !automationFilterData.length ||
        (automationFilterData &&
          automationFilterData.length &&
          automationFilterData.length === 1)
      ) {
        this.showErrorAlert(
          'frontend_groovemail_automations_error_add_conditions_and_actions'
        );
      } else if (!automationFiltersObj.filtersConfigured) {
        this.showErrorAlert(
          'frontend_groovemail_automations_error_all_nodes_not_configured_fully'
        );
      } else if (!automationFiltersObj.filtersHasActions) {
        this.showErrorAlert(
          'frontend_groovemail_automations_error_add_atleast_one_trigger_and_action'
        );
      }
      return automationValid;
    },

    showErrorAlert(errorMessage) {
      showAlert({
        iconType: 'alert-error',
        html: `
          <style>.swal2-confirm { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark"> ${this.$t(errorMessage)}</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },

    checkAutomationFiltersConfigured(filters) {
      let filtersConfigured = true;
      let filtersHasActions = false;
      filters.forEach((element) => {
        filtersConfigured = filtersConfigured && element.filterValid;
        if (element.type === 'action') {
          filtersHasActions = true;
        }
      });
      return {
        filtersConfigured,
        filtersHasActions,
      };
    },

    closeAutomationNameModal() {
      this.$root.$emit('modal-close', 'automation-modal');
      this.$v.model.name.$reset();
      this.model.name = '';
    },
  },

  components: {
    Automate,
    ExitIcon,
  },
};
</script>
